import React, { useEffect, useState } from 'react';
import {
  Box,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { MdEdit } from 'react-icons/md';
import { confirmWrapper } from 'src/components/Dialog';
import {
  CloseActiveIcon,
  CloseSquareIcon,
  ConfirmActiveIcon,
  ConfirmSquareIcon,
  ExpandUpIcon,
} from 'src/icons';
import { useCurrencies } from 'src/hooks/query/currencies';

const InlineInput = function (props: any) {
  const {
    value = '',
    label,
    inlineLabel = '',
    confirmFunction,
    isEditable = true,
    currencyOption,
    showCurrencyOption = false,
  } = props;

  const { currenciesOptions } = useCurrencies({ enabled: showCurrencyOption });

  const [tempValue, setTempValue] = useState(value);
  const [newValue, setNewValue] = useState(value);
  const [editMode, togleEditMode] = useState(false);

  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isConfirmHovered, setIsConfirmHovered] = useState(false);

  const [selectedCurrency, setSelectedCurrency] = useState(currencyOption || currenciesOptions[0]);
  const handleCurrencyChange = (event: any) => {
    setSelectedCurrency(event.target.value as typeof currenciesOptions[0]);
  };

  const currencySymbol = currencyOption ? currencyOption.currencyIcon : '';

  const handleMouseEnterClose = () => {
    setIsCloseHovered(true);
  };

  const handleMouseLeaveClose = () => {
    setIsCloseHovered(false);
  };

  const handleMouseEnterConfirm = () => {
    setIsConfirmHovered(true);
  };

  const handleMouseLeaveConfirm = () => {
    setIsConfirmHovered(false);
  };

  useEffect(() => {
    setNewValue(value);
    setSelectedCurrency(currencyOption || currenciesOptions[0]);
    setTempValue('');
    togleEditMode(false);
  }, [value, currencyOption, currenciesOptions]);

  const onCancel = () => {
    setTempValue('');
    setSelectedCurrency(currencyOption || currenciesOptions[0]);
    togleEditMode(false);
  };

  const onClickUpdate = async () => {
    const finalValue = (tempValue || '').trim();
    if (finalValue.length === 0) {
      await confirmWrapper(
        `Please enter valid input for ${label || inlineLabel}`,
        {
          variant: 'warning',
          primaryBtnLabel: 'Okay',
          hideSecondaryBtn: true,
        },
      );
      return;
    }

    const confirmation = await confirmWrapper(
      `Are you sure you want to update ${label || inlineLabel}?`,
      {
        variant: 'warning',
        primaryBtnLabel: 'Proceed',
        secondaryBtnLabel: 'Cancel',
      },
    );

    if (confirmation) {
      try {
        await confirmFunction(finalValue, selectedCurrency);
        setNewValue(finalValue);
        togleEditMode(false);
      } catch (e) {
        console.log(e);
      }
    }
  };

  return (
    <Box className="w-full h-full group">
      {!inlineLabel ? (
        <>
          {label && (
            <Box className="flex gap-3 items-center">
              <Box className="text-sm leading-5 text-[#828A97] pb-1">
                {label}
              </Box>
              {isEditable && !editMode && (
                <Box
                  className="cursor-pointer pb-1 group-hover:visible invisible"
                  onClick={() => {
                    setTempValue(newValue);
                    togleEditMode((prevState) => !prevState);
                  }}
                >
                  <MdEdit size="14" color="#828A97" />
                </Box>
              )}
            </Box>
          )}
          {
            editMode ? (
              <Box className="relative w-full h-full">
                <TextField
                  className="w-full h-full"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                />
                <Box className="absolute flex gap-[2px] right-0">
                  <Box
                    className="cursor-pointer"
                    onMouseEnter={handleMouseEnterClose}
                    onMouseLeave={handleMouseLeaveClose}
                    onClick={onCancel}
                  >
                    {isCloseHovered ? (
                      <CloseActiveIcon size={20} />
                    ) : (
                      <CloseSquareIcon size={20} />
                    )}
                  </Box>
                  <Box
                    className="cursor-pointer"
                    onMouseEnter={handleMouseEnterConfirm}
                    onMouseLeave={handleMouseLeaveConfirm}
                    onClick={onClickUpdate}
                  >
                    {isConfirmHovered ? (
                      <ConfirmActiveIcon size={20} />
                    ) : (
                      <ConfirmSquareIcon size={20} />
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box className="flex">
                <Box className="text-base overflow-hidden text-ellipsis">
                  {currencySymbol}
                  {' '}
                  {newValue}
                </Box>
              </Box>
            )
          }
        </>
      ) : (
        <Box className="relative w-full h-full">
          <Box className="flex items-center gap-3">
            <Box className="grow-0 shrink-0 text-sm leading-5 text-[#828A97]">
              {inlineLabel}
              :
            </Box>
            {!editMode ? (
              <>
                <Box className="text-base overflow-hidden text-ellipsis">
                  {currencySymbol}
                  {' '}
                  {newValue}
                </Box>

                {isEditable && (
                  <Box
                    className="cursor-pointer flex items-center group-hover:visible invisible"
                    onClick={() => {
                      setTempValue(newValue);
                      togleEditMode((prevState) => !prevState);
                    }}
                  >
                    <MdEdit size="14" color="#828A97" />
                  </Box>
                )}
              </>
            ) : (
              <Box className="relative w-full h-full">
                <Box className="flex gap-1 align-bottom">
                  {showCurrencyOption && (
                    <Select
                      value={selectedCurrency}
                      onChange={handleCurrencyChange}
                      IconComponent={ExpandUpIcon}
                      style={{
                        backgroundColor: '#F6F6F6',
                        borderTopLeftRadius: '4px',
                        borderBottomLeftRadius: '4px',
                        fontSize: '12px',
                        height: '26px',
                      }}
                      inputProps={{ 'aria-label': 'Without label' }}
                      classes={{

                        icon: 'mr-0 mb-2 w-3 h-3',
                      }}
                    >
                      {currenciesOptions.map((option) => (
                        <MenuItem key={option.currencyName} className="text-[#28364C]" value={option as any}>
                          {option.currencyName}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  <TextField
                    className="w-full h-full min-w-[80px]"
                    inputProps={{
                      style: {
                        paddingLeft: 2,
                        paddingRight: 2,
                        paddingTop: 2,
                        paddingBottom: 2,
                      },
                    }}
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                  />
                </Box>
                <Box className="absolute flex gap-[2px] right-0">
                  <Box
                    className="cursor-pointer"
                    onMouseEnter={handleMouseEnterClose}
                    onMouseLeave={handleMouseLeaveClose}
                    onClick={onCancel}
                  >
                    {isCloseHovered ? (
                      <CloseActiveIcon size={20} />
                    ) : (
                      <CloseSquareIcon size={20} />
                    )}
                  </Box>
                  <Box
                    className="cursor-pointer"
                    onMouseEnter={handleMouseEnterConfirm}
                    onMouseLeave={handleMouseLeaveConfirm}
                    onClick={onClickUpdate}
                  >
                    {isConfirmHovered ? (
                      <ConfirmActiveIcon size={20} />
                    ) : (
                      <ConfirmSquareIcon size={20} />
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InlineInput;
